import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { UserDetailsFromCorpSystem } from 'src/types/admin/accessManagement.types';
import { validateEmployeeLoginWithPAPI } from 'src/utils/employeeServiceUtils';
import { logger } from 'src/utils/logger';
import { RootState } from '../store';

interface CachedUserInfo {
  data: UserDetailsFromCorpSystem | null;
  isFetching: boolean;
  error: boolean;
  lastFetchTimestamp: number;
}

interface UserInfoCacheState {
  cache: Record<string, CachedUserInfo>;
}

const initialState: UserInfoCacheState = {
  cache: {},
};

/**
 * Async thunk to fetch user info from PAPI and cache it.
 * Returns null if user should be ignored or validation fails.
 */
export const fetchUserInfo = createAsyncThunk(
  'userInfoCache/fetchUserInfo',
  async (login: string, { rejectWithValue }) => {
    try {
      const validationResponse = await validateEmployeeLoginWithPAPI(login);

      if (validationResponse.success && validationResponse.userInfo) {
        return {
          login,
          userInfo: validationResponse.userInfo,
        };
      } else {
        logger.warn('UserInfoCache: User validation failed', {
          login,
          validationMessage: validationResponse.validationMessage,
        });
        return rejectWithValue('User validation failed');
      }
    } catch (err) {
      logger.error('UserInfoCache: Failed to fetch user info', { login, error: err });
      return rejectWithValue((err as Error).message || 'Failed to fetch user info');
    }
  }
);

const userInfoCacheSlice = createSlice({
  name: 'userInfoCache',
  initialState,
  reducers: {
    clearUserInfoCache: (state) => {
      state.cache = {};
    },
    removeUserFromCache: (state, action) => {
      delete state.cache[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserInfo.pending, (state, action) => {
        const login = action.meta.arg;
        state.cache[login] = {
          data: state.cache[login]?.data || null,
          isFetching: true,
          error: false,
          lastFetchTimestamp: state.cache[login]?.lastFetchTimestamp || Date.now(),
        };
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        const { login, userInfo } = action.payload;
        state.cache[login] = {
          data: userInfo,
          isFetching: false,
          error: false,
          lastFetchTimestamp: Date.now(),
        };
      })
      .addCase(fetchUserInfo.rejected, (state, action) => {
        const login = action.meta.arg;
        state.cache[login] = {
          data: null,
          isFetching: false,
          error: true,
          lastFetchTimestamp: Date.now(),
        };
      });
  },
});

// Selectors
export const selectUserInfoFromCache = (state: RootState, login: string): CachedUserInfo | undefined => {
  return state.userInfoCache.cache[login];
};

export const selectIsUserInfoCached = (state: RootState, login: string): boolean => {
  return login in state.userInfoCache.cache;
};

export const selectAllCachedUsers = (state: RootState): Record<string, CachedUserInfo> => {
  return state.userInfoCache.cache;
};

// Actions
export const { clearUserInfoCache, removeUserFromCache } = userInfoCacheSlice.actions;

// Reducer
export default userInfoCacheSlice.reducer;
